import React, { useCallback, useState, useMemo } from 'react';
import { useTranslation } from '../../../../shared/i18n';
import ExerciseCounter from '../../../physical/ExerciseCounter';
import { completeSession, saveProgress } from '../../api/activitySessions';

/**
 * PhysicalSession - Wrapper for ExerciseCounter that handles session completion
 */
export const PhysicalSession = ({
  session,
  activity,
  onComplete
}) => {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Get required reps from activity config - must be provided by backend
  const requiredReps = activity.required_reps;
  if (!requiredReps) {
    throw new Error('PhysicalSession: required_reps must be provided by the backend');
  }

  // Get initial reps from final_snapshot (checkpoint from previous stop/pause)
  const initialReps = session.final_snapshot?.reps || 0;

  // Get debugging flag from activity config
  const debuggingPhysicalActivity = activity.debugging_physical_activity || false;

  // Streaming upload URL for recording debug footage
  const streamingUploadUrl = debuggingPhysicalActivity ? '/api/physical_footage_uploads' : null;

  // Determine exercise type from activity_sub_type
  // activity_sub_type is "skipping", "pickleball", or "soccer"
  const exerciseType = activity.activity_sub_type;

  // Build labels from translations for ExerciseCounter
  // Use exercise-type specific translations
  const isPickleball = exerciseType === 'pickleball';
  const isSoccer = exerciseType === 'soccer';

  // Helper to get type-specific translation
  const getTypeSpecificLabel = (skippingKey, pickleballKey, soccerKey) => {
    if (isSoccer) return t(soccerKey);
    if (isPickleball) return t(pickleballKey);
    return t(skippingKey);
  };

  const labels = useMemo(() => ({
    jump_rope_counter: getTypeSpecificLabel(
      'children.exercise_counter.jump_rope_counter',
      'children.exercise_counter.pickleball_counter',
      'children.exercise_counter.soccer_counter'
    ),
    exercise_counter: t('children.exercise_counter.exercise_counter'),
    start_exercise: t('children.exercise_counter.start_exercise'),
    start_with_streaming: t('children.exercise_counter.start_with_streaming'),
    stop_exercise: t('children.exercise_counter.stop_exercise'),
    resume_exercise: t('children.exercise_counter.resume_exercise'),
    resume_with_streaming: t('children.exercise_counter.resume_with_streaming'),
    finish_exercise: t('children.exercise_counter.finish_exercise'),
    test_sound: t('children.exercise_counter.test_sound'),
    report_inaccuracy: t('children.exercise_counter.report_inaccuracy'),
    start_over: t('children.exercise_counter.start_over'),
    reps_completed: getTypeSpecificLabel(
      'children.exercise_counter.reps_completed',
      'children.exercise_counter.strokes_completed',
      'children.exercise_counter.juggles_completed'
    ),
    goal: t('children.exercise_counter.goal'),
    loading_model: getTypeSpecificLabel(
      'children.exercise_counter.loading_model',
      'children.exercise_counter.loading_model_pickleball',
      'children.exercise_counter.loading_model_soccer'
    ),
    camera_tip: getTypeSpecificLabel(
      'children.exercise_counter.camera_tip',
      'children.exercise_counter.camera_tip_pickleball',
      'children.exercise_counter.camera_tip_soccer'
    ),
    goal_achieved: t('children.exercise_counter.goal_achieved'),
    camera_not_started: t('children.exercise_counter.camera_not_started'),
    did_you_hear_beep: t('children.exercise_counter.did_you_hear_beep'),
    yes: t('children.exercise_counter.yes'),
    no: t('children.exercise_counter.no'),
    sound_enabled: isPickleball
      ? t('children.exercise_counter.sound_enabled_pickleball')
      : t('children.exercise_counter.sound_enabled'),
    check_silent_mode: t('children.exercise_counter.check_silent_mode'),
    ok: t('children.exercise_counter.ok'),
    start: t('children.exercise_counter.start'),
    camera_sees_whole_body: getTypeSpecificLabel(
      'children.exercise_counter.camera_sees_whole_body',
      'children.exercise_counter.paddle_ball_visible',
      'children.exercise_counter.ball_feet_visible'
    ),
    beep_on_correct_jump: getTypeSpecificLabel(
      'children.exercise_counter.beep_on_correct_jump',
      'children.exercise_counter.beep_on_correct_stroke',
      'children.exercise_counter.beep_on_correct_juggle'
    )
  }), [t, isPickleball, isSoccer]);

  const handleGameComplete = useCallback(async (gameResult) => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    try {
      // Build final snapshot with completion data
      const finalSnapshot = {
        completed: gameResult.completed,
        reps: gameResult.reps
      };

      // Complete the session via API
      const result = await completeSession(session.id, finalSnapshot);

      if (result.status === 'success') {
        // Success - notify parent to refresh dashboard
        onComplete(result);
      } else {
        // Handle unexpected status
        console.error('Unexpected completion status:', result.status);
        alert(result.message || 'Failed to complete activity');
      }
    } catch (error) {
      console.error('Error completing session:', error);
      alert(error.message || 'Failed to complete activity');
    } finally {
      setIsSubmitting(false);
    }
  }, [session.id, onComplete, isSubmitting]);

  const handleProgressSave = useCallback(async (progressData) => {
    try {
      await saveProgress(session.id, progressData);
    } catch (error) {
      console.error('Error saving progress:', error);
      // Don't alert for progress saves - they're not critical
    }
  }, [session.id]);

  // Admin-selected inference engine for this physical activity, resolved
  // server-side (Setting.physical_inference_engine_for) and shipped in the
  // /api/children/.../activity_sessions payload. When absent, ExerciseCounter
  // uses its own default. This is the value that actually drives useDetector.
  const inferenceEngine = activity.inference_engine;

  return (
    <ExerciseCounter
      activityId={activity.id}
      sessionId={session.id}
      railsEnv={window.railsEnv || 'development'}
      requiredReps={requiredReps}
      initialReps={initialReps}
      onComplete={handleGameComplete}
      onProgressSave={handleProgressSave}
      exerciseType={exerciseType}
      labels={labels}
      showReportButton={debuggingPhysicalActivity}
      streamingUploadUrl={streamingUploadUrl}
      showMaxFPSOption={debuggingPhysicalActivity}
      {...(inferenceEngine ? { engine: inferenceEngine } : {})}
    />
  );
};
